import AppComponent from './app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { ApiResult } from '../lib/api-client.ts';

/**
 * Classe base per componenti di ricerca.
 *
 * Gestisce: input con debounce, chiamata API, stati loading/errore,
 * rendering risultati in container dedicato, cleanup.
 *
 * La sottoclasse fornisce: resultsTemplateId, fetchResults(), mapResultsData().
 * Opzionalmente sovrascrive: minChars, debounceMs, onSearchComplete().
 */
export default abstract class SearchComponent<T = unknown> extends AppComponent {

  // --- Configurazione (override opzionale) ---

  protected get minChars(): number { return 3; }
  protected get debounceMs(): number { return 300; }

  // --- Abstract (override richiesto) ---

  /** ID del template NJK per i risultati */
  protected abstract get resultsTemplateId(): string;

  /** Chiamata API specifica */
  protected abstract fetchResults(query: string): Promise<ApiResult<T>>;

  /** Trasforma risposta API in dati per il template risultati */
  protected abstract mapResultsData(data: T): Record<string, unknown>;

  // --- Stato interno ---

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastQuery = '';

  // --- Lifecycle ---

  protected override afterRender(): void {
    this.delegate.on<InputEvent>('input', '[data-search-input]', (e) => {
      const query = (e.target as HTMLInputElement).value.trim();
      this.scheduleSearch(query);
    });

    this.onReady();
  }

  /** Hook per le sottoclassi: binding aggiuntivi dopo il render iniziale */
  protected onReady(): void {}

  /** Hook dopo rendering risultati riuscito */
  protected onSearchComplete(_container: HTMLElement, _data: Record<string, unknown>): void {}

  protected override onDisconnected(): void {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // --- Logica ricerca ---

  private scheduleSearch(query: string): void {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);

    if (query.length < this.minChars) {
      this._lastQuery = '';
      this.clearResults();
      return;
    }

    if (query === this._lastQuery) return;

    this._debounceTimer = setTimeout(() => this.executeSearch(query), this.debounceMs);
  }

  private async executeSearch(query: string): Promise<void> {
    this._lastQuery = query;
    const container = this.$('[data-search-results]');
    if (!container) return;

    // Loading
    container.innerHTML =
      '<div class="d-flex justify-content-center py-3">' +
      '<div class="spinner-border spinner-border-sm text-primary" role="status">' +
      '<span class="visually-hidden">Ricerca...</span></div></div>';

    const result = await this.fetchResults(query);

    if (!result.ok) {
      container.innerHTML =
        `<div class="alert alert-danger" role="alert">${result.error}</div>`;
      return;
    }

    const data = this.mapResultsData(result.data);
    templateParseToElement(this.resultsTemplateId, container, data);
    this.onSearchComplete(container, data);
  }

  private clearResults(): void {
    const container = this.$('[data-search-results]');
    if (container) container.innerHTML = '';
  }
}
